import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "@tanstack/react-router";
import { Flex, Text } from "styles/utility-components";

import Button from "components/ui/button/Button";
import Collapsible from "components/ui/collapsible/Collapsible";
import Input from "components/ui/input/Input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select/Select";
import toast from "components/ui/toast/Toast";
import Toggle from "components/ui/toggle/Toggle";
import { authBaseUrl } from "lib/auth/authClient";
import {
  type OidcConfigInput,
  oidcFormCreateSchema,
  oidcFormSchema,
  type OidcFormValues,
} from "lib/hooks/api/auth/sso/schemas";
import { useRegisterSsoProvider } from "lib/hooks/api/auth/sso/useRegisterSsoProvider";
import { useUpdateSsoProvider } from "lib/hooks/api/auth/sso/useUpdateSsoProvider";
import { useAssertCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import type { ProviderPreset } from "./ProviderSelector";
import { extractEmailDomain, generateProviderId } from "./ProviderUtils";
import { CopyableField, ErrorText, FieldGroup, Form, TagInput, ToggleRow } from "./SharedStyles";

function formValuesToOidcConfig(values: OidcFormValues, scopes: string[]) {
  return {
    clientId: values.clientId,
    ...(values.clientSecret && { clientSecret: values.clientSecret }),
    tokenEndpointAuthentication: values.tokenEndpointAuthentication,
    pkce: values.pkce,
    ...(scopes.length && { scopes }),
    ...(values.skipDiscovery && {
      skipDiscovery: true,
      authorizationEndpoint: values.authorizationEndpoint || undefined,
      tokenEndpoint: values.tokenEndpoint || undefined,
      userInfoEndpoint: values.userInfoEndpoint || undefined,
      jwksEndpoint: values.jwksEndpoint || undefined,
    }),
  };
}

function oidcConfigToFormDefaults(config?: Partial<OidcConfigInput>): Partial<OidcFormValues> {
  return {
    clientId: config?.clientId ?? "",
    tokenEndpointAuthentication: config?.tokenEndpointAuthentication ?? "client_secret_basic",
    scopes: "",
    pkce: config?.pkce ?? true,
    skipDiscovery: config?.skipDiscovery ?? false,
    authorizationEndpoint: config?.authorizationEndpoint ?? "",
    tokenEndpoint: config?.tokenEndpoint ?? "",
    userInfoEndpoint: config?.userInfoEndpoint ?? "",
    jwksEndpoint: config?.jwksEndpoint ?? "",
  };
}

type OidcRegistrationFormProps = {
  preset?: ProviderPreset;
  editProvider?: {
    providerId: string;
    issuer: string;
    domain: string;
    oidcConfig?: Partial<OidcConfigInput> & { clientIdLastFour?: string };
  };
};

export default function OidcRegistrationForm({ preset, editProvider }: OidcRegistrationFormProps) {
  const navigate = useNavigate();
  const currentUser = useAssertCurrentUser();
  const organizationId = currentUser.company.guid;
  const defaultDomain = extractEmailDomain(currentUser.email);

  const isEditing = !!editProvider;

  const initialDomains = editProvider
    ? editProvider.domain.split(",").map((d) => d.trim())
    : defaultDomain
      ? [defaultDomain]
      : [];

  const initialScopes = editProvider?.oidcConfig?.scopes ?? ["openid", "profile", "email"];

  const [formError, setFormError] = useState<string | null>(null);

  const [domains, setDomains] = useState<string[]>(initialDomains);
  const [domainTouched, setDomainTouched] = useState(false);
  const [scopes, setScopes] = useState<string[]>(initialScopes);

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<OidcFormValues>({
    resolver: zodResolver(isEditing ? oidcFormSchema : oidcFormCreateSchema),
    mode: "onBlur",
    defaultValues: {
      issuer: editProvider?.issuer ?? preset?.defaultValues?.issuer ?? "",
      clientSecret: "",
      ...oidcConfigToFormDefaults(editProvider?.oidcConfig),
    },
  });

  const providerName = preset?.providerName ?? "custom";
  const oidcProviderId = generateProviderId(organizationId, "oidc", providerName);
  const callbackUrl = `${authBaseUrl}/sso/callback/${oidcProviderId}`;

  const skipDiscovery = watch("skipDiscovery");

  const { mutate: registerProvider, isLoading: isRegisteringProvider } = useRegisterSsoProvider({
    onSuccess: async () => {
      toast.success("SSO provider registered");
      await navigate({ to: "/account-management/sso" });
    },
    onError: (error) => {
      setFormError(error.message ?? "Could not register SSO provider");
    },
  });

  const { mutate: updateProvider, isLoading: isUpdatingProvider } = useUpdateSsoProvider({
    onSuccess: async () => {
      toast.success("SSO provider updated");
      await navigate({ to: "/account-management/sso" });
    },
    onError: (error) => {
      setFormError(error.message ?? "Could not update SSO provider");
    },
  });

  const onSubmit = handleSubmit((values) => {
    setFormError(null);

    const domainString = domains.join(",");
    const oidcConfig = formValuesToOidcConfig(values, scopes);

    if (isEditing) {
      // better-auth doesn't return the full clientId, so omit it when blank to avoid overwriting
      // Instead it returns clientIdLastFour - no clue why only the last 4 chars since this isn't a secret
      // and you can quite easily get the clientId from the URL when doing an auth request
      const { clientId, ...restConfig } = oidcConfig;
      updateProvider({
        providerId: editProvider.providerId,
        issuer: values.issuer,
        domain: domainString,
        oidcConfig: clientId ? oidcConfig : restConfig,
      });
    } else {
      registerProvider({
        providerId: oidcProviderId,
        issuer: values.issuer,
        domain: domainString,
        organizationId,
        oidcConfig,
        overrideUserInfo: true,
      });
    }
  });

  const isPending = isRegisteringProvider || isUpdatingProvider;

  return (
    <Flex column gap={24}>
      <Flex column gap={8}>
        <Text h1>
          {isEditing
            ? "Edit OIDC SSO provider"
            : `Register OIDC provider${preset?.name ? `: ${preset.name}` : ""}`}
        </Text>
        <Text description>
          {isEditing
            ? "Update the configuration for this SSO provider."
            : "Configure an OpenID Connect provider for single sign-on."}
        </Text>
      </Flex>

      <Form onSubmit={onSubmit}>
        <Flex column gap={16}>
          <FieldGroup>
            <label>Callback URL</label>
            <Text description>Add this URL to your identity provider's allowed redirect URIs.</Text>
            <CopyableField value={callbackUrl} toastMessage="Callback URL copied to clipboard" />
          </FieldGroup>

          <FieldGroup>
            <label htmlFor="issuer">Issuer URL</label>
            <Input
              control={control}
              name="issuer"
              placeholder={preset?.placeholders?.issuer ?? "https://login.example.com"}
            />
            {errors.issuer && <ErrorText>{errors.issuer.message}</ErrorText>}
          </FieldGroup>

          <FieldGroup>
            <label htmlFor="clientId">
              Client ID{isEditing ? " (leave blank to keep existing)" : ""}
            </label>
            <Input
              control={control}
              name="clientId"
              placeholder={editProvider?.oidcConfig?.clientIdLastFour ?? "your-client-id"}
            />
            {errors.clientId && <ErrorText>{errors.clientId.message}</ErrorText>}
          </FieldGroup>

          <FieldGroup>
            <label htmlFor="clientSecret">
              Client secret{isEditing ? " (leave blank to keep existing)" : ""}
            </label>
            <Input
              control={control}
              name="clientSecret"
              type="password"
              autoComplete="off"
              placeholder={isEditing ? "••••••••" : "your-client-secret"}
            />
            {errors.clientSecret && <ErrorText>{errors.clientSecret.message}</ErrorText>}
          </FieldGroup>

          <FieldGroup>
            <label>Allowed email domains</label>
            <Text description>
              Users with email addresses on these domains are forced to use this SSO connection.
              Subdomains are automatically included.
            </Text>
            <TagInput
              values={domains}
              onChange={setDomains}
              placeholder="example.com"
              onBlur={() => setDomainTouched(true)}
            />
            {domainTouched && domains.length === 0 && (
              <ErrorText>At least one domain is required</ErrorText>
            )}
          </FieldGroup>
        </Flex>

        <Collapsible
          title="Advanced settings"
          defaultOpen={false}
          isBordered
          showBottomDivider={false}
          contentWrapperStyles={{
            display: "flex",
            flexDirection: "column",
            gap: 16,
            paddingTop: 16,
          }}
        >
          <FieldGroup>
            <label>Token endpoint authentication</label>
            <Select control={control} name="tokenEndpointAuthentication">
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="client_secret_basic">Client Secret Basic</SelectItem>
                <SelectItem value="client_secret_post">Client Secret Post</SelectItem>
              </SelectContent>
            </Select>
          </FieldGroup>

          <FieldGroup>
            <label>Scopes</label>
            <TagInput
              values={scopes}
              onChange={setScopes}
              placeholder="openid"
              separators={["Enter", " "]}
              lowercase={false}
            />
          </FieldGroup>

          <ToggleRow>
            <Flex column gap={2}>
              <label>PKCE</label>
              <Text description>Proof Key for Code Exchange (recommended)</Text>
            </Flex>
            <Controller
              control={control}
              name="pkce"
              render={({ field }) => (
                <Toggle checked={field.value} onCheckedChange={field.onChange} />
              )}
            />
          </ToggleRow>

          <ToggleRow>
            <Flex column gap={2}>
              <label>Skip OIDC discovery</label>
              <Text description>Manually specify endpoints instead of using .well-known</Text>
            </Flex>
            <Controller
              control={control}
              name="skipDiscovery"
              render={({ field }) => (
                <Toggle checked={field.value} onCheckedChange={field.onChange} />
              )}
            />
          </ToggleRow>

          {skipDiscovery && (
            <>
              <FieldGroup>
                <label htmlFor="authorizationEndpoint">Authorization endpoint</label>
                <Input control={control} name="authorizationEndpoint" placeholder="https://..." />
              </FieldGroup>
              <FieldGroup>
                <label htmlFor="tokenEndpoint">Token endpoint</label>
                <Input control={control} name="tokenEndpoint" placeholder="https://..." />
              </FieldGroup>
              <FieldGroup>
                <label htmlFor="userInfoEndpoint">UserInfo endpoint</label>
                <Input control={control} name="userInfoEndpoint" placeholder="https://..." />
              </FieldGroup>
              <FieldGroup>
                <label htmlFor="jwksEndpoint">JWKS endpoint</label>
                <Input control={control} name="jwksEndpoint" placeholder="https://..." />
              </FieldGroup>
            </>
          )}
        </Collapsible>

        <Flex gap={8}>
          <Button type="submit" loading={isPending} disabled={domains.length === 0}>
            {isEditing ? "Save changes" : "Register provider"}
          </Button>
          <Button
            as="a"
            to={isEditing ? "/account-management/sso" : "/account-management/sso/add"}
            buttonType="secondary"
            activeOptions={{ exact: true }}
          >
            Cancel
          </Button>
        </Flex>
        {formError && <ErrorText>{formError}</ErrorText>}
      </Form>
    </Flex>
  );
}
